import React from "react";
import { MdClose } from "react-icons/md";
import { fixedDecimals, convertTime } from "../../../../helpers/helpers";
import { useTranslation } from "react-i18next";

const QuoteDetails = ({ selectedQuote, carriers, onClose }) => {
  const { t } = useTranslation("admin");
  if (!selectedQuote) return null;

  const getCarrierLogo = (carrierName) => {
    if (!carrierName) return null;
    const nameLower = carrierName.toLowerCase();
    const carrier = carriers?.find?.(
      (c) =>
        c?.name?.toLowerCase?.().includes(nameLower) ||
        nameLower.includes(c?.name?.toLowerCase?.()),
    );
    return carrier?.logo_url || null;
  };

  const parseAddress = (addressData) => {
    if (!addressData) return "-";

    // The structure can be { address: { ... } } or just { ... }
    const addr =
      addressData.address && typeof addressData.address === "object"
        ? addressData.address
        : addressData;

    // 1. Check if the object itself is a string
    if (typeof addr === "string") return addr;

    // 2. Check for full address strings
    const full =
      addr.fullAddress ||
      addr.full_address ||
      addressData.fullAddress ||
      addressData.full_address;
    if (full && typeof full === "string") return full;

    // 3. Fallback to parts
    const parts = [
      typeof addr.address === "string" ? addr.address : null,
      addr.city,
      addr.state_code || addr.state || addr.stateCode,
      addr.zip,
      addr.country_code || addr.country || addr.countryCode,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(", ") : "-";
  };

  const unitSystem = selectedQuote?.description?.other?.unit?.toLowerCase();
  const weightUnit = unitSystem === "metric" ? "Kg" : "Lb";
  const sizeUnit = unitSystem === "metric" ? "cm" : "in";

  // Parse quotes data for the comparison table
  let items = [];
  const quotesData = selectedQuote?.quotes;
  if (Array.isArray(quotesData)) {
    items = quotesData.filter(Boolean);
  } else if (typeof quotesData === "object" && quotesData !== null) {
    items = Object.entries(quotesData).map(([carrier, val]) => {
      if (typeof val === "object" && val !== null) {
        return { carrier, amount: val, type: val.type };
      }
      return { carrier, amount: val, type: null };
    });
  }

  const renderContactInfo = (contact) => {
    if (!contact) return null;
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
        <div className="flex flex-col">
          <span className="text-ui-text-subtle font-semibold uppercase text-[8px]">
            {t("quotes.attention")}
          </span>
          <span className="text-ui-text font-medium">
            {contact.attention || "-"}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-ui-text-subtle font-semibold uppercase text-[8px]">
            {t("quotes.company")}
          </span>
          <span className="text-ui-text font-medium">
            {contact.company || "-"}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-ui-text-subtle font-semibold uppercase text-[8px]">
            {t("quotes.phone")}
          </span>
          <span className="text-ui-text font-medium">
            {contact.phone || "-"}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-ui-text-subtle font-semibold uppercase text-[8px]">
            {t("quotes.email")}
          </span>
          <span className="text-ui-text font-medium break-all">
            {contact.email || "-"}
          </span>
        </div>
      </div>
    );
  };

  const renderStepProgress = () => {
    const steps = [
      { key: "fetched_quotes", label: t("quotes.quotesFetched") },
      { key: "selected_quote", label: t("quotes.quoteSelected") },
      { key: "quote_card_landed", label: t("quotes.nextStep") },
      { key: "contact_details_added", label: t("quotes.contactDetails") },
      { key: "payment_successful", label: t("quotes.paymentDone") },
    ];

    const currentStepKey = selectedQuote?.description?.other?.step;
    const currentStepIndex = steps.findIndex((s) => s.key === currentStepKey);

    return (
      <div className="px-1 py-1">
        <div className="flex items-center justify-between relative mb-2">
          {/* Background Line */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-ui-surface-sunken -z-0"></div>

          {steps.map((step, index) => {
            const isCompleted = index <= currentStepIndex;
            const isCurrent = index === currentStepIndex;

            return (
              <div
                key={step.key}
                className="flex flex-col items-center relative z-10"
              >
                <div
                  className={`w-4 h-4 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                    isCompleted
                      ? "bg-brand border-brand text-white"
                      : "bg-ui-surface border-ui-border"
                  }`}
                >
                  {isCompleted && (
                    <div className="w-1.5 h-1.5 bg-ui-surface rounded-full"></div>
                  )}
                </div>
                <span
                  className={`text-[8px] mt-1 font-bold uppercase tracking-tighter transition-colors duration-500 whitespace-nowrap ${
                    isCompleted ? "text-brand" : "text-ui-border"
                  }`}
                >
                  {step.label}
                </span>
                {isCurrent && (
                  <div className="absolute -top-1 w-6 h-6 bg-brand-light rounded-full animate-pulse -z-10"></div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-ui-surface rounded-lg shadow-xl w-full max-h-[95vh] overflow-hidden flex flex-col">
      {/* Premium Header */}
      <div className="bg-brand px-4 py-2.5 shrink-0">
        <div className="flex items-center justify-between">
          <div className="w-8"></div>
          <div className="text-center">
            <h1 className="text-sm font-semibold text-white uppercase tracking-wide">
              {t("quotes.recordNumber", { id: selectedQuote.id })}
            </h1>
            <p className="text-[9px] text-white/70">
              {t("quotes.createdUpdated", {
                created: convertTime(selectedQuote.created_at),
                updated: convertTime(selectedQuote.updated_at),
              })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/10 rounded-full p-1 transition-all"
          >
            <MdClose size={20} />
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="p-3 overflow-y-auto space-y-3 scroll-smooth thin__scrollbar">
        {/* Step Progress */}
        {renderStepProgress()}

        {/* Compact Summary Bar */}
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 px-3 py-1.5 bg-ui-surface-sunken rounded-lg border border-ui-surface-sunken text-[10px]">
          <div className="flex gap-1">
            <span className="text-ui-text-subtle font-semibold uppercase">
              {t("quotes.weightLabel")}
            </span>{" "}
            <span className="text-ui-text">
              {selectedQuote?.description?.other?.total_weight ?? "-"}{" "}
              {weightUnit}
            </span>
          </div>
          <div className="flex gap-1">
            <span className="text-ui-text-subtle font-semibold uppercase">
              {t("quotes.systemLabel")}
            </span>{" "}
            <span className="text-ui-text uppercase">
              {unitSystem || "N/A"}
            </span>
          </div>
          <div className="flex gap-1">
            <span className="text-ui-text-subtle font-semibold uppercase">{t("quotes.typeLabel")}</span>{" "}
            <span className="text-ui-text uppercase">
              {selectedQuote?.type || "N/A"}
            </span>
          </div>
          <div className="flex gap-1">
            <span className="text-ui-text-subtle font-semibold uppercase">
              {t("quotes.signatureLabel")}
            </span>{" "}
            <span className="text-ui-text">
              {selectedQuote?.description?.other?.signature ? t("quotes.yesCaps") : t("quotes.noCaps")}
            </span>
          </div>
          <div className="flex gap-1">
            <span className="text-ui-text-subtle font-semibold uppercase">
              {t("quotes.insuranceLabel")}
            </span>{" "}
            <span className="text-ui-text">
              {selectedQuote?.description?.other?.insurance ? t("quotes.yesCaps") : t("quotes.noCaps")}
            </span>
          </div>
        </div>

        {/* Addresses Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 ml-1">
              <div className="h-1.5 w-1.5 rounded-full bg-state-success"></div>
              <div className="text-[10px] font-semibold text-ui-text-subtle uppercase tracking-widest">
                {t("quotes.origin")}
              </div>
            </div>
            <div className="bg-ui-surface border border-ui-surface-sunken p-2.5 rounded-lg shadow-sm space-y-2">
              <p className="text-[11px] font-medium text-ui-text leading-normal">
                {parseAddress(selectedQuote?.description?.from_address)}
              </p>

              {renderContactInfo(
                selectedQuote?.description?.contact_details?.pickup,
              )}

              <div className="flex items-center gap-3 mt-1.5">
                <span className="text-[9px] bg-brand-light text-brand px-1.5 py-0.5 rounded font-semibold border border-brand tracking-wider uppercase">
                  {selectedQuote?.description?.from_address?.zip ||
                    selectedQuote?.description?.from_address?.address?.zip}
                </span>
                <span className="text-[10px] text-ui-text-subtle font-normal">
                  {selectedQuote?.description?.from_address?.residential
                    ? t("quotes.residential")
                    : t("quotes.business")}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 ml-1">
              <div className="h-1.5 w-1.5 rounded-full bg-state-danger"></div>
              <div className="text-[10px] font-semibold text-ui-text-subtle uppercase tracking-widest">
                {t("quotes.destination")}
              </div>
            </div>
            <div className="bg-ui-surface border border-ui-surface-sunken p-2.5 rounded-lg shadow-sm space-y-2">
              <p className="text-[11px] font-medium text-ui-text leading-normal">
                {parseAddress(selectedQuote?.description?.to_address)}
              </p>

              {renderContactInfo(
                selectedQuote?.description?.contact_details?.delivery,
              )}

              <div className="flex items-center gap-3 mt-1.5">
                <span className="text-[9px] bg-brand-light text-brand px-1.5 py-0.5 rounded font-semibold border border-brand tracking-wider uppercase">
                  {selectedQuote?.description?.to_address?.zip ||
                    selectedQuote?.description?.to_address?.address?.zip}
                </span>
                <span className="text-[10px] text-ui-text-subtle font-normal">
                  {selectedQuote?.description?.to_address?.residential
                    ? t("quotes.residential")
                    : t("quotes.business")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Package Specifications */}
        {selectedQuote?.description?.dimensions?.length > 0 && (
          <div className="space-y-1.5">
            <h3 className="text-[10px] font-semibold text-ui-text-subtle uppercase tracking-widest ml-1">
              {t("quotes.packageDetails")}
            </h3>
            <div className="space-y-1">
              {selectedQuote.description.dimensions.map((dim, i) => (
                <div
                  key={i}
                  className="flex items-center bg-ui-surface-sunken p-2 rounded-lg border border-ui-surface-sunken text-[11px]"
                >
                  <span className="text-ui-text-subtle font-medium uppercase tracking-tighter w-12 text-[9px]">
                    {t("quotes.boxNumber", { number: i + 1 })}
                  </span>
                  <div className="flex-1 flex justify-center gap-6">
                    <span className="text-ui-text">
                      <span className="text-ui-text-subtle text-[9px] mr-1">L:</span>
                      {dim?.length ?? "-"}
                      <span className="text-ui-text-subtle text-[9px] mx-1">W:</span>
                      {dim?.width ?? "-"}
                      <span className="text-ui-text-subtle text-[9px] mx-1">H:</span>
                      {dim?.height ?? "-"}
                      <small className="text-ui-text-subtle ml-1.5">{sizeUnit}</small>
                    </span>
                    <span className="text-ui-border font-thin text-xs">|</span>
                    <span className="text-ui-text">
                      {dim?.weight ?? "-"}{" "}
                      <small className="text-ui-text-subtle">{weightUnit}</small>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Carrier price Breakdown Table */}
        <div className="space-y-1.5">
          <h3 className="text-[10px] font-semibold text-ui-text-subtle uppercase tracking-widest ml-1">
            {t("quotes.carrierComparison")}
          </h3>
          <div className="border border-ui-surface-sunken rounded-lg overflow-hidden shadow-sm bg-ui-surface">
            <table className="w-full text-xs text-left">
              <thead className="bg-ui-surface-sunken text-ui-text-subtle font-semibold border-b border-ui-surface-sunken text-[9px] uppercase tracking-widest">
                <tr>
                  <th className="px-4 py-2">{t("quotes.carrierBreakdown")}</th>
                  <th className="px-4 py-2">{t("quotes.serviceType")}</th>
                  <th className="px-4 py-2 text-right">{t("quotes.finalPrice")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ui-surface-sunken">
                {items.map((q, i) => {
                  const isObj =
                    typeof q.amount === "object" && q.amount !== null;
                  const total = isObj
                    ? (q.amount.total ?? q.amount.amount ?? q.amount.sub_total)
                    : q.amount;
                  const breakdown = isObj
                    ? Object.entries(q.amount).filter(
                        ([k]) =>
                          !["total", "amount", "type"].includes(
                            k.toLowerCase(),
                          ),
                      )
                    : [];

                  return (
                    <tr
                      key={i}
                      className={`hover:bg-ui-surface-sunken transition-all ${q.selected ? "bg-brand/[0.03]" : ""}`}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-3">
                            {getCarrierLogo(q.carrier) ? (
                              <img
                                src={getCarrierLogo(q.carrier)}
                                alt={q.carrier}
                                className="h-4 sm:h-5 object-contain"
                              />
                            ) : (
                              <span className="font-semibold text-[10px] text-brand">
                                {q.carrier?.toUpperCase()}
                              </span>
                            )}

                            {q.selected && (
                              <span className="bg-state-success-bg text-state-success text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-tighter">
                                {t("quotes.selected")}
                              </span>
                            )}

                            {/* Breakdown inline with logo */}
                            {breakdown.length > 0 && (
                              <div className="flex gap-3 ml-2 border-l border-ui-surface-sunken pl-3">
                                {breakdown.map(([key, val]) => (
                                  <div
                                    key={key}
                                    className="flex items-center gap-1.5"
                                  >
                                    <span className="text-[8px] text-ui-text-subtle uppercase font-medium">
                                      {key.replace(/_/g, " ")}:
                                    </span>
                                    <span className="text-[10px] text-ui-text-muted font-medium">
                                      ${fixedDecimals(val, 2)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-ui-text-muted font-medium uppercase text-[10px] tracking-tight">
                          {q.type || t("quotes.standardCaps")}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold text-ui-text text-sm">
                        ${fixedDecimals(total, 2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Metadata Section */}
        {selectedQuote?.meta && Object.keys(selectedQuote.meta).length > 0 && (
          <div className="space-y-1.5">
            <h3 className="text-[10px] font-semibold text-ui-text-subtle uppercase tracking-widest ml-1">
              {t("quotes.additionalMetadata")}
            </h3>
            <div className="bg-ui-surface-sunken rounded-lg p-3 border border-ui-surface-sunken flex flex-wrap gap-4 items-start">
              {Object.entries(selectedQuote.meta).map(([key, value]) => (
                <div key={key} className="flex flex-col min-w-[100px]">
                  <span className="text-[8px] text-ui-text-subtle font-semibold uppercase tracking-widest mb-0.5">
                    {key.replace(/_/g, " ")}
                  </span>
                  <span className="text-[10px] text-ui-text font-medium break-all">
                    {typeof value === "object"
                      ? JSON.stringify(value)
                      : String(value)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteDetails;
